import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { createPortal } from "react-dom";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  NavigationAPI,
  BuildingAPI,
  FeedbackAPI,
  FloorAPI,
  ShapeAPI,
  PathClosureAPI,
  DestinationAPI,
  resolveAsset,
} from "../../services/api";
import LeafletFloorMap from "../../components/LeafletFloorMap";
import QrScannerModal from "../../components/QrScannerModal";
import ClosurePickerMap from "../../components/ClosurePickerMap";
import RouteChoiceModal from "../../components/RouteChoiceModal";
import { isQrActive } from "../../utils/qrStatus";
import { parseNames, translateText } from "../../utils/nameParser";
import { findS1Building, getDefaultBuilding } from "../../utils/buildingSelection";
import { ReactComponent as LocationIcon } from "../../assets/icons/userlayout/location.svg";
import { ReactComponent as FeedbackIcon } from "../../assets/icons/layout1/feedback.svg";
import { ReactComponent as HomeworkIcon } from "../../assets/icons/userfeedback/homework.svg";
import { ReactComponent as BlockIcon } from "../../assets/icons/userfeedback/block.svg";
import { ReactComponent as MoreIcon } from "../../assets/icons/userfeedback/more.svg";
import { ReactComponent as QrcodeIcon } from "../../assets/icons/user2/qrcode.svg";
import { ReactComponent as CameraIcon } from "../../assets/icons/userfeedback/camera.svg";
import { ReactComponent as SendIcon } from "../../assets/icons/userfeedback/send.svg";
import { ReactComponent as BarrierIcon } from "../../assets/icons/userfeedback/barrier.svg";
import { ReactComponent as MapIcon } from "../../assets/icons/userfeedback/map.svg";
import { ReactComponent as BuildingIcon } from "../../assets/icons/layout1/building.svg";

const denorm = (v, scale) => (v <= 1 ? v * scale : v);

const toSafeDbId = (value) => {
  const id = Number(value);
  return Number.isInteger(id) && id > 0 && id <= 2147483647 ? id : undefined;
};

const getShapeCenter = (shape, imgW = 1000, imgH = 700) => {
  if (Array.isArray(shape.points) && shape.points.length > 0) {
    const sumX = shape.points.reduce((sum, p) => sum + denorm(p.x, imgW), 0);
    const sumY = shape.points.reduce((sum, p) => sum + denorm(p.y, imgH), 0);
    return {
      x: sumX / shape.points.length,
      y: sumY / shape.points.length,
    };
  }
  const x = denorm(shape.x || 0, imgW);
  const y = denorm(shape.y || 0, imgH);
  const w = denorm(shape.width || 0, imgW);
  const h = denorm(shape.height || 0, imgH);
  return {
    x: x + w / 2,
    y: y + h / 2,
  };
};

const shapeBelongsToFloor = (shape, floorId) => {
  const shapeFloorId = shape.floorId ?? shape.floor_id ?? shape.floor?.id;
  return shapeFloorId == null || String(shapeFloorId) === String(floorId);
};

function normalizeFloorName(value) {
  return String(value || "")
    .normalize("NFKC")
    .trim()
    .toLowerCase()
    .replace(/floor|ชั้น|ชั้นที่/g, "")
    .replace(/[^\p{L}\p{N}]+/gu, "");
}

function floorComparableValue(floor) {
  const level = Number(floor?.level ?? floor?.floorLevel);
  if (Number.isFinite(level)) return `level:${level}`;
  const name = normalizeFloorName(floor?.name || floor?.floorName);
  return name ? `name:${name}` : "";
}

function destinationNeedsVerticalRoute(dest, scannedFloorId, currentFloorId, floors) {
  const destFloorId = dest?.floorId || currentFloorId;
  if (String(destFloorId) === String(scannedFloorId)) return false;

  const scannedFloor = floors.find((floor) => String(floor.id) === String(scannedFloorId));
  const destFloor = floors.find((floor) => String(floor.id) === String(destFloorId));
  const scannedComparable = floorComparableValue(scannedFloor);
  const destComparable = floorComparableValue(dest) || floorComparableValue(destFloor);
  if (scannedComparable && destComparable) return scannedComparable !== destComparable;

  return String(destFloorId) !== String(scannedFloorId);
}

export default function MobileMainPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const buildingId = searchParams.get("buildingId");
  const floorId = searchParams.get("floorId");
  const currentQrId = searchParams.get("currentQrId");
  // ชั้นที่สแกน QR จริงๆ (ต่างจาก floorId ที่อาจเปลี่ยนตอนสลับชั้น)
  const scannedFloorId = searchParams.get("scannedFloorId") || searchParams.get("floorId");
  const mapIconsParam = searchParams.get("mapIcons") || "";

  const [buildings, setBuildings] = useState([]);
  const [floors, setFloors] = useState([]);
  const [floorData, setFloorData] = useState(null);
  const [shapes, setShapes] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [closures, setClosures] = useState([]); // active path closures บนชั้นปัจจุบัน
  const [activeMarker, setActiveMarker] = useState(null);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [feedbackType, setFeedbackType] = useState("");
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [feedbackImage, setFeedbackImage] = useState(null);
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackError, setFeedbackError] = useState("");
  // ทางปิด: ตำแหน่งที่ user เลือกบนแผนที่ + state เปิด picker
  const [closureSelection, setClosureSelection] = useState(null);
  const [closurePickerOpen, setClosurePickerOpen] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [floorsExpanded, setFloorsExpanded] = useState(false);
  const [routePreference, setRoutePreference] = useState(""); // "elevator" | "stair"
  const [routeModalOpen, setRouteModalOpen] = useState(false);
  const [pendingDestination, setPendingDestination] = useState(null);
  const [mapFullscreen, setMapFullscreen] = useState(false);
  const mapBeforeS1Ref = useRef(null);
  const [destinations, setDestinations] = useState([]);
  const [serverDestinations, setServerDestinations] = useState([]);
  const selectedDestId = searchParams.get("destId");
  // eslint-disable-next-line no-unused-vars
  const selectedDestFloorId = searchParams.get("destFloorId");
  const visibleMapIcons = useMemo(
    () => new Set(mapIconsParam.split(",").filter(Boolean)),
    [mapIconsParam]
  );

  // โหลดสถานที่เพิ่มเติมทั้งหมดของชั้นนี้
  useEffect(() => {
    if (!floorId) {
      setServerDestinations([]);
      return;
    }
    DestinationAPI.listByFloor(floorId)
      .then((d) => setServerDestinations(Array.isArray(d) ? d : []))
      .catch(() => setServerDestinations([]));
  }, [floorId]);

  // โหลดสถานที่ปลายทาง (destinations) จาก shapes (พื้นที่วาด) เพื่อแสดงหมุดและขอบเขตพื้นที่
  // โดยอิงข้อมูลรูปภาพ หมวดหมู่ และชื่อจาก Additional Location ที่เชื่อมโยงกันไว้
  useEffect(() => {
    const imgW = floorData?.imageWidth || 1000;
    const imgH = floorData?.imageHeight || 700;
    const activeFloor = floors.find((f) => String(f.id) === String(floorId));

    const mapped = shapes
      .filter((s) => !s.hidden && s.name)
      .map((s) => {
        const center = getShapeCenter(s, imgW, imgH);

        // ค้นหา destination ที่ลิงก์กัน
        let linkedDest = null;
        if (s.destinationId) {
          linkedDest = serverDestinations.find((d) => String(d.id) === String(s.destinationId));
        } else {
          // fallback: จับคู่ตามชื่ออย่างเดียว (เผื่อประเภทของ shape เช่น 'room' ไม่ตรงกับ 'counterservice')
          linkedDest = serverDestinations.find((d) => d.name === s.name);
        }

        const name = linkedDest?.name || s.name;
        const category = linkedDest?.category || s.category || "room";
        const description = linkedDest?.description || s.description || "";
        const rawImg = linkedDest?.image || linkedDest?.imageUrl || linkedDest?.img || s.imageUrl || s.image || s.img || "";

        return {
          id: s.id, // ต้องเป็น s.id เพื่อใช้ในระบบนำทางตามปกติ
          name,
          category,
          description,
          x: Math.round(center.x),
          y: Math.round(center.y),
          imageUrl: resolveAsset(rawImg) || null,
          floorId,
          floorLevel: activeFloor?.level,
          floorName: activeFloor?.name,
          shape: s,
        };
      });
    setDestinations(mapped);
  }, [shapes, serverDestinations, floorData, floorId, floors, i18n.language]);

  // ตั้งค่า activeMarker อัตโนมัติเมื่อกดเลือกจากกล่องค้นหา (destId)
  useEffect(() => {
    if (!selectedDestId || !destinations.length) return;
    const dest = destinations.find((d) => String(d.id) === String(selectedDestId));
    if (dest) {
      setActiveMarker({ type: dest.category || "poi", data: dest });
    }
  }, [selectedDestId, destinations]);

  // โหลดอาคารทั้งหมด
  useEffect(() => {
    BuildingAPI.list({ pageSize: 50 })
      .then((r) => {
        const list = Array.isArray(r) ? r : r?.items || [];
        setBuildings(list);
        if (!buildingId && list.length > 0) {
          const defaultBuilding = getDefaultBuilding(list);
          setSearchParams(
            { buildingId: String(defaultBuilding.id) },
            { replace: true }
          );
        }
      })
      .catch(() => { });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // โหลดชั้นของอาคาร
  useEffect(() => {
    if (!buildingId) return;
    FloorAPI.listByBuilding(buildingId)
      .then((data) => {
        const list = Array.isArray(data) ? data : data?.items || [];
        setFloors(list);
        const currentFloorIdInUrl = new URLSearchParams(
          window.location.search
        ).get("floorId");
        const exists = list.some(
          (f) => String(f.id) === String(currentFloorIdInUrl)
        );
        if (list.length > 0 && (!currentFloorIdInUrl || !exists)) {
          setSearchParams(
            { buildingId, floorId: String(list[0].id) },
            { replace: true }
          );
        }
      })
      .catch(() => setFloors([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildingId]);

  // โหลด floor data + shapes + active closures
  useEffect(() => {
    if (!floorId) {
      setFloorData(null);
      setShapes([]);
      setClosures([]);
      return;
    }
    setShapes([]);
    setClosures([]);
    NavigationAPI.getFloor(floorId)
      .then(setFloorData)
      .catch(() => setFloorData(null));
    ShapeAPI.listByFloor(floorId)
      .then((d) =>
        setShapes(
          (Array.isArray(d) ? d : []).filter((shape) =>
            shapeBelongsToFloor(shape, floorId)
          )
        )
      )
      .catch(() => setShapes([]));
    PathClosureAPI.listByFloor(floorId, "active")
      .then((d) => setClosures(Array.isArray(d) ? d : d?.items || []))
      .catch(() => setClosures([]));
  }, [floorId]);

  const currentBuilding = buildings.find(
    (b) => String(b.id) === String(buildingId)
  );
  const currentFloor = floors.find((f) => String(f.id) === String(floorId));

  const openS1FullscreenMap = async () => {
    const s1Building = findS1Building(buildings);

    if (!s1Building) {
      alert("ไม่พบอาคาร S1 ในระบบ");
      return;
    }

    try {
      mapBeforeS1Ref.current = {
        buildingId,
        floorId,
      };
      const data = await FloorAPI.listByBuilding(s1Building.id);
      const s1Floors = Array.isArray(data) ? data : data?.items || [];
      const firstFloor = [...s1Floors].sort(
        (a, b) => Number(a.level || 0) - Number(b.level || 0)
      )[0];

      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("buildingId", String(s1Building.id));
        if (firstFloor) next.set("floorId", String(firstFloor.id));
        next.delete("destId");
        next.delete("destFloorId");
        return next;
      });
      setActiveMarker(null);
      setMapFullscreen(true);
    } catch {
      alert("ไม่สามารถเปิดแผนที่ S1 ได้ กรุณาลองใหม่อีกครั้ง");
    }
  };

  const closeS1FullscreenMap = () => {
    const previousMap = mapBeforeS1Ref.current;
    setMapFullscreen(false);
    if (previousMap?.buildingId) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("buildingId", String(previousMap.buildingId));
        if (previousMap.floorId) {
          next.set("floorId", String(previousMap.floorId));
        } else {
          next.delete("floorId");
        }
        return next;
      });
    }
    mapBeforeS1Ref.current = null;
  };

  // eslint-disable-next-line no-unused-vars
  const switchFloor = (id) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams();
        if (buildingId) next.set("buildingId", String(buildingId));
        next.set("floorId", String(id));
        // คงค่า currentQrId + scannedFloorId ไว้เพื่อให้แสดงห้องทุกชั้นหลัง scan
        const qrId = prev.get("currentQrId");
        const sfId = prev.get("scannedFloorId");
        if (qrId) next.set("currentQrId", qrId);
        if (sfId) next.set("scannedFloorId", sfId);
        return next;
      },
      { replace: true }
    );
  };

  // เตรียม markers จาก floorData (QR codes) และ destinations (POI: stairs/elevators/bathroom/etc)
  const markers = useMemo(() => {
    if (!floorData) return [];

    const isRestroomDestination = (point) => {
      const text = `${point.category || ""} ${point.name || ""} ${point.text || ""}`.toLowerCase();
      return text.includes("restroom") || text.includes("toilet") || text.includes("bathroom") || text.includes("wc") || text.includes("ห้องน้ำ");
    };

    // ===== โหมดค้นหา/เลือกปลายทาง =====
    if (selectedDestId) {
      const selectedDest = destinations.find((d) => String(d.id) === String(selectedDestId));
      if (!selectedDest) return [];

      const isStair = selectedDest.category === "stair" || selectedDest.category === "stairs";
      const isElevator = selectedDest.category === "elevator";
      const isRestroom = isRestroomDestination(selectedDest);

      let markerType = selectedDest.category || "poi";
      if (isRestroom) markerType = "bathroom";
      else if (isStair) markerType = "stair";
      else if (isElevator) markerType = "elevator";

      return [{
        id: `dest-${selectedDest.id}`,
        type: markerType,
        x: selectedDest.x,
        y: selectedDest.y,
        label: parseNames(selectedDest.name || selectedDest.text).primary || "สถานที่",
        imageUrl: resolveAsset(selectedDest.imageUrl || selectedDest.image || selectedDest.img) || null,
        onClick: () => setActiveMarker({ type: markerType, data: selectedDest }),
      }];
    }

    // ===== โหมดปกติ / หลัง scan QR: รวม room markers + toggle markers =====
    const out = [];
    const showStairs = visibleMapIcons.has("stair");
    const showElevators = visibleMapIcons.has("elevator");
    const showRestrooms = visibleMapIcons.has("restroom");

    destinations.forEach((d) => {
      const cat = String(d.category || "").trim().toLowerCase();
      const isStair = cat === "stair" || cat === "stairs";
      const isElevator = cat === "elevator" || cat === "lift";
      const isRestroom = isRestroomDestination(d);
      const isRoom = !isStair && !isElevator && !isRestroom;

      let markerType = d.category || "room";
      if (isRestroom) markerType = "bathroom";
      else if (isStair) markerType = "stair";
      else if (isElevator) markerType = "elevator";

      // แสดง room ทุกห้องหลัง scan QR
      const showByQr = currentQrId && isRoom;
      // แสดงตาม toggle
      const showByToggle =
        (isStair && showStairs) ||
        (isElevator && showElevators) ||
        (isRestroom && showRestrooms);

      if (!showByQr && !showByToggle) return;

      out.push({
        id: `dest-${currentQrId ? "all-" : ""}${d.id}`,
        type: markerType,
        x: d.x,
        y: d.y,
        label: parseNames(d.name || d.text).primary || "",
        imageUrl: resolveAsset(d.imageUrl || d.image || d.img) || null,
        onClick: () => setActiveMarker({ type: markerType, data: d }),
      });
    });

    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [floorData, destinations, visibleMapIcons, selectedDestId, currentQrId, i18n.language]);

  const imageUrl = resolveAsset(floorData?.imageUrl) || null;

  // หาตำแหน่ง "คุณอยู่ที่นี่" จาก currentQrId
  const currentQrPoint = useMemo(() => {
    if (!currentQrId || !floorData?.qr) return null;
    return floorData.qr.find((p) => String(p.id) === String(currentQrId) && isQrActive(p)) || null;
  }, [currentQrId, floorData]);

  const userPosition = useMemo(
    () => currentQrPoint ? { x: currentQrPoint.x, y: currentQrPoint.y } : null,
    [currentQrPoint]
  );

  // เมื่อ scanner resolve สำเร็จ → สลับ building/floor + ปัก current
  const onScanResolved = async ({ qr, floor, building }) => {
    setScannerOpen(false);
    const resolvedFloorId = floor?.id || qr.floorId || floorId;
    const next = {
      currentQrId: String(qr.id),
      // บันทึกชั้นที่สแกนจริงแยกต่างหาก ใช้ตอนหา QR position สำหรับ navigation
      scannedFloorId: String(resolvedFloorId),
    };
    const existingDestId = searchParams.get("destId");
    const existingDestFloorId = searchParams.get("destFloorId");
    const nextBuildingId = building?.id || qr.buildingId || buildingId;
    const nextFloorId = resolvedFloorId;
    if (building?.id) next.buildingId = String(building.id);
    else if (qr.buildingId) next.buildingId = String(qr.buildingId);
    next.floorId = String(resolvedFloorId);
    if (existingDestId) next.destId = existingDestId;
    if (existingDestFloorId) next.destFloorId = existingDestFloorId;

    if (
      existingDestId &&
      existingDestFloorId &&
      nextFloorId &&
      String(existingDestFloorId) === String(nextFloorId) &&
      Number.isFinite(Number(qr.x)) &&
      Number.isFinite(Number(qr.y))
    ) {
      try {
        const [shapeData, targetFloor] = await Promise.all([
          ShapeAPI.listByFloor(existingDestFloorId).catch(() => []),
          NavigationAPI.getFloor(existingDestFloorId).catch(() => null),
        ]);
        const list = (Array.isArray(shapeData) ? shapeData : []).filter((item) =>
          shapeBelongsToFloor(item, existingDestFloorId)
        );
        const shape = list.find((item) => String(item.id) === String(existingDestId));

        if (shape) {
          const center = getShapeCenter(
            shape,
            targetFloor?.imageWidth || 1000,
            targetFloor?.imageHeight || 700
          );
          const params = new URLSearchParams({
            ...(nextBuildingId ? { buildingId: String(nextBuildingId) } : {}),
            floorId: String(existingDestFloorId),
            targetFloorId: String(existingDestFloorId),
            fromQrId: String(qr.id),
            toId: String(existingDestId),
            startX: String(Math.round(Number(qr.x))),
            startY: String(Math.round(Number(qr.y))),
            endX: String(Math.round(center.x)),
            endY: String(Math.round(center.y)),
            destName: shape.name || t("user.mainPage.destination"),
          });
          navigate(`/user/usernavigation?${params.toString()}`, { replace: true });
          return;
        }
      } catch {
        // If the auto-continue lookup fails, keep the user on the map with the destination selected.
      }
    }

    setSearchParams(next, { replace: false });
  };

  const feedbackOptions = [
    { id: "ตำแหน่งไม่ตรง", Icon: LocationIcon, label: t("user.feedback.problems.locationMismatch") },
    { id: "ห้องย้ายที่", Icon: HomeworkIcon, label: t("user.feedback.problems.roomMoved") },
    { id: "ทางปิด", Icon: BlockIcon, label: t("user.feedback.problems.closedRoute") },
    { id: "อื่น ๆ", Icon: MoreIcon, label: t("user.feedback.problems.other") },
  ];

  // เริ่มนำทางจาก marker ที่คลิกบนแผนที่ (ต้องมี QR scan ก่อน)
  const doNavigateToMarker = async (dest, mode) => {
    try {
      // ดึง QR position จากชั้นที่สแกนจริง (scannedFloorId) ไม่ใช่ชั้นที่กำลังดูอยู่
      const fData = await NavigationAPI.getFloor(scannedFloorId).catch(() => null);
      const qr = (fData?.qr || []).find(
        (p) => String(p.id) === String(currentQrId) && isQrActive(p)
      );
      if (!qr) {
        alert(t("user.mainPage.qrPositionNotFound"));
        return;
      }

      // log route (เหมือนกับ search navigation)
      const routeLogPayload = {
        floorId: Number(scannedFloorId),
        startQrId: Number(qr.id),
        startName: qr.name,
        destinationName: parseNames(dest.name, true).primary,
        userSessionId: getSessionId(),
      };
      const shapeId = toSafeDbId(dest.shapeId ?? dest.id);
      if (shapeId) routeLogPayload.shapeId = shapeId;
      NavigationAPI.logRoute(routeLogPayload).catch(() => {});

      const destFloorId = dest.floorId || floorId;
      const params = new URLSearchParams({
        buildingId: String(buildingId),
        floorId: String(scannedFloorId),       // ชั้นที่เริ่มต้น (จาก QR)
        fromQrId: String(currentQrId),
        toId: String(dest.id),
        startX: String(Math.round(Number(qr.x))),
        startY: String(Math.round(Number(qr.y))),
        endX: String(Math.round(Number(dest.x))),
        endY: String(Math.round(Number(dest.y))),
        targetFloorId: String(destFloorId),    // ชั้นปลายทาง
        destName: String(dest.name || t("user.mainPage.destination")),
        routeMode: mode || "elevator",
      });
      navigate(`/user/usernavigation?${params.toString()}`);
    } catch {
      alert(t("user.navigation.cannotCalculate"));
    }
  };


  const startNavigationFromMarker = (dest, chosenPreference) => {
    if (!currentQrId) {
      alert(t("user.mainPage.scanQrFirst"));
      return;
    }
    setPendingDestination(dest);
    const isCrossFloor = destinationNeedsVerticalRoute(dest, scannedFloorId, floorId, floors);
    const prefToUse = chosenPreference || (isCrossFloor ? routePreference : "elevator");

    if (isCrossFloor && !prefToUse) {
      setRouteModalOpen(true);
      return;
    }

    doNavigateToMarker(dest, prefToUse || "elevator");
    setRoutePreference(""); // Reset preference after navigation
    setRouteModalOpen(false);
    setPendingDestination(null);
  };

  const handleSelectRouteMode = (mode) => {
    setRoutePreference(mode);
    setRouteModalOpen(false);
    const d = pendingDestination || activeMarker?.data || (
      activeMarker
        ? destinations.find((item) => String(item.id) === String(activeMarker?.id || activeMarker))
        : null
    );
    if (d) {
      startNavigationFromMarker(d, mode);
    }
  };


  const closeFeedback = () => {
    setFeedbackOpen(false);
    setFeedbackSubmitted(false);
    setFeedbackType("");
    setFeedbackMessage("");
    setFeedbackImage(null);
    setFeedbackError("");
    setClosureSelection(null);
  };

  const isClosedRoute = feedbackType === "ทางปิด";

  const submitFeedback = async () => {
    setFeedbackError("");

    if (!feedbackType) {
      setFeedbackError(t("user.feedback.selectProblemError"));
      return;
    }
    if (isClosedRoute && !closureSelection) {
      setFeedbackError("กรุณาเลือกตำแหน่งทางปิดบนแผนที่");
      return;
    }

    setFeedbackSubmitting(true);
    try {
      await FeedbackAPI.submit({
        category: feedbackType,
        description: feedbackMessage || feedbackType,
        text: feedbackMessage || feedbackType,
        title: feedbackType,
        image: feedbackImage,
        buildingId,
        floorId,
        x: userPosition?.x,
        y: userPosition?.y,
        ...(isClosedRoute && closureSelection
          ? {
            closure: {
              floorId: closureSelection.floorId,
              x: closureSelection.x,
              y: closureSelection.y,
              radiusPx: 80, // ขยายรัศมีกัน edge ที่ตัดผ่านจุดถูกตรวจไม่พบ
              status: "pending",
            },
          }
          : {}),
      });
      setFeedbackSubmitted(true);
    } catch (error) {
      setFeedbackError(error?.message || t("user.feedback.submitFailed"));
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  return (
    <div className="w-full h-full relative bg-slate-100 overflow-hidden">
      {/* Map (เต็มจอ, ภายใต้ header/footer-) */}
      <div className="absolute inset-0">
        <LeafletFloorMap
          imageUrl={imageUrl}
          imageWidth={floorData?.imageWidth}
          imageHeight={floorData?.imageHeight}
          shapes={shapes}
          markers={markers}
          userPosition={userPosition}
          focusPoint={userPosition}
          focusKey={currentQrId}
          focusZoomOffset={0.2}
          className="w-full h-full"
          showShapes={false}
        />
      </div>

      {mapFullscreen && createPortal(
        <div className="fixed inset-0 z-[9999] bg-slate-100">
          <LeafletFloorMap
            imageUrl={imageUrl}
            imageWidth={floorData?.imageWidth}
            imageHeight={floorData?.imageHeight}
            shapes={shapes}
            markers={markers}
            userPosition={userPosition}
            focusPoint={userPosition}
            focusKey={`s1-full-${floorId || ""}-${currentQrId || ""}`}
            focusZoomOffset={0.2}
            className="h-full w-full"
            showShapes={false}
          />
          <button
            type="button"
            onClick={closeS1FullscreenMap}
            className="absolute bottom-[145px] right-2 z-[10000] grid h-11 w-11 place-items-center rounded-xl border-2 border-blue-600 bg-blue-600 text-xl font-bold text-white shadow-xl hover:bg-blue-700"
            aria-label="กลับไปแผนที่ปัจจุบัน"
            title="กลับไปแผนที่ปัจจุบัน"
          >
            ⛶
          </button>
        </div>,
        document.body
      )}

      {/* ===== Marker Detail Bottom Sheet (คลิก marker บนแผนที่) ===== */}
      {activeMarker && !selectedDestId && (() => {
        const d = activeMarker.data;
        // ดึง serverDestination ตรงๆ เพื่อให้ได้รูปที่อัปโหลดไว้
        const linked = serverDestinations.find(
          (sd) =>
            (d?.destinationId && String(sd.id) === String(d.destinationId)) ||
            sd.name === d?.name
        );
        const rawImg = linked?.image || linked?.imageUrl || linked?.img || d?.imageUrl || "";
        const imgUrl = resolveAsset(rawImg) || null;

        return (
          <div
            className="fixed inset-0 z-[1500]"
            onClick={() => {
              setActiveMarker(null);
              setRoutePreference("");
            }}
          >
            <div
              className="fixed bottom-0 left-0 right-0 rounded-t-2xl bg-white shadow-[0_-8px_24px_rgba(15,23,42,0.14)] border border-slate-200 p-2 pb-[calc(env(safe-area-inset-bottom)+16px)]"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mx-auto my-2 h-1 w-12 rounded-full bg-slate-300" />

              {imgUrl ? (
                <img
                  src={imgUrl}
                  alt=""
                  className="w-full h-28 rounded-lg object-cover mb-4"
                />
              ) : (
                <div className="w-full h-28 rounded-lg bg-slate-100 border border-slate-100 flex items-center justify-center mb-4">
                  <BuildingIcon className="w-9 h-9 text-slate-300" />
                </div>
              )}

              {(() => {
                const { primary, aliases } = parseNames(d?.name);
                return (
                  <div className="px-2">
                    <div className="font-bold text-base text-slate-900 leading-snug">
                      {primary || "ห้อง"}
                    </div>
                    {aliases.length > 0 && (
                      <div className="text-xs text-slate-500 font-normal mt-0.5">
                        {t("user.layout.otherNames", "ชื่ออื่น:")} {aliases.join(", ")}
                      </div>
                    )}
                  </div>
                );
              })()}

              <div className="mt-2 px-2 flex items-center justify-between gap-3">
                <div className="min-w-0 flex items-center gap-2">
                  {(() => {
                    const floorObj = floors.find((f) => String(f.id) === String(d?.floorId));
                    const floorName = translateText(floorObj?.name || (d?.floorId ? `ชั้น ${floorObj?.level || d.floorId}` : ""));
                    return (
                      <>
                        {floorName && (
                          <span className="shrink-0 rounded-md bg-blue-50 px-2 py-1 text-xs font-bold text-blue-600">
                            {floorName}
                          </span>
                        )}
                      </>
                    );
                  })()}
                  <span className="truncate text-xs text-slate-500">
                    {translateText(currentBuilding?.name) || d?.category}
                  </span>
                </div>
              </div>

              <div className="mt-4 px-2">
                <div className="text-sm font-bold text-slate-900">
                  {t("user.menuList.description", "อธิบาย")}
                </div>
                <div className="mt-1 text-sm text-slate-600 leading-relaxed">
                  {linked?.description || d?.description || currentBuilding?.name}
                </div>
              </div>

              <button
                onClick={() => startNavigationFromMarker(d)}
                className="mt-4 w-full h-12 rounded-xl bg-blue-600 hover:bg-blue-700 active:scale-[0.99] text-white font-bold text-base flex items-center justify-center gap-2 shadow-md transition"
              >
                <span className="mr-2">⚑</span>
                {t("user.menuList.startNavigation", "เริ่มนำทาง")}
              </button>
            </div>
          </div>
        );
      })()}



      {/* ===== Bottom location card ===== */}
      <div className="fixed bottom-2 left-2 right-2 z-[1000] flex flex-col gap-1.5">
        <div className="flex w-full rounded-xl bg-slate-900/85 shadow-lg justify-start items-center p-3">
          <div className="w-10 h-10 p-1.5 text-lg rounded-lg bg-white shadow flex items-center justify-center shrink-0">
            <LocationIcon className="w-4 h-4 text-blue-600" />
          </div>
          <div className="text-start ml-3 min-w-0 flex-1">
            <p className="text-[10px] font-bold text-sky-400 uppercase tracking-wide">
              {t("user.mainPage.yourLocation")}
            </p>
            <p className="text-sm sm:text-base font-bold text-white truncate">
              {translateText(currentBuilding?.name) || t("user.mainPage.noBuildingSelected")}
            </p>
            <p className="text-[10px] font-semibold text-slate-300">
              {currentFloor
                ? translateText(currentFloor.name || `ชั้น ${currentFloor.level}`)
                : "—"}
            </p>
          </div>
        </div>
        <button
          onClick={() => setScannerOpen(true)}
          className="flex w-full rounded-lg bg-slate-900 hover:bg-black shadow-lg justify-center items-center py-2.5"
        >
          <QrcodeIcon className="w-5 h-5 text-white mr-2" />
          <p className="text-white font-bold text-sm sm:text-base">
            {t("user.mainPage.scanToConfirm")}
          </p>
        </button>
      </div>

      {/* ===== Floor switcher (ขวาล่าง) ===== */}
      <div className="absolute bottom-[145px] right-2 z-[1000] flex flex-col items-end gap-2">
        <button
          type="button"
          onClick={openS1FullscreenMap}
          className="grid h-11 w-11 place-items-center rounded-xl border border-slate-300 bg-slate-200 text-xl font-bold text-slate-600 shadow-lg hover:bg-slate-300"
          aria-label="ขยายแผนที่ S1"
          title="ขยายแผนที่ S1"
        >
          ⛶
        </button>
        <button
          onClick={() => setFeedbackOpen(true)}
          className="w-10 h-10 rounded-xl bg-white shadow-lg border border-slate-200 flex items-center justify-center text-2xl text-slate-500 hover:text-blue-600"
          aria-label={t("user.mainPage.reportProblem")}
        >
          <FeedbackIcon className="w-5 h-5 text-slate-700" />
        </button>
      </div>

      {/* ===== Scanner modal ===== */}
      {scannerOpen && (
        <QrScannerModal
          onClose={() => setScannerOpen(false)}
          onResolved={onScanResolved}
        />
      )}

      {/* ===== Closure picker (ทางปิด) ===== */}
      {closurePickerOpen && (
        <ClosurePickerMap
          defaultBuildingId={buildingId || undefined}
          defaultFloorId={floorId || undefined}
          onCancel={() => setClosurePickerOpen(false)}
          onConfirm={(data) => {
            setClosureSelection(data);
            setClosurePickerOpen(false);
          }}
        />
      )}

      {/* ===== Feedback modal ===== */}
      {feedbackOpen && createPortal(
        <div className="fixed inset-0 z-[3000] bg-white md:bg-slate-950/45 md:flex md:items-center md:justify-center md:p-4">
          {feedbackSubmitted ? (
            <div className="relative mx-auto flex h-[100dvh] w-full max-w-[430px] flex-col items-center overflow-hidden bg-white px-5 pb-[calc(env(safe-area-inset-bottom)+92px)] pt-[calc(env(safe-area-inset-top)+56px)] md:h-[min(820px,calc(100vh-32px))] md:rounded-2xl md:shadow-2xl">
              <button
                onClick={closeFeedback}
                className="absolute left-5 top-[calc(env(safe-area-inset-top)+20px)] z-10 grid h-10 w-10 place-items-center rounded-full text-3xl leading-none text-slate-500 hover:bg-slate-100"
                aria-label="ปิด"
              >
                ×
              </button>

              <div className="relative mt-3 flex aspect-square w-[min(74vw,300px)] max-w-[300px] shrink-0 items-center justify-center">
                <div className="absolute inset-0 rounded-full bg-emerald-50" />
                <div className="absolute inset-8 rounded-full bg-sky-100/70" />
                <div className="absolute left-5 top-1/2 w-4 h-4 rounded-full bg-yellow-200" />
                <div className="absolute right-10 top-14 w-5 h-5 rounded-full bg-blue-300" />
                <div className="absolute left-16 bottom-14 w-8 h-8 rounded-full bg-emerald-200" />
                <div className="relative flex h-[44%] w-[44%] items-center justify-center rounded-3xl bg-white shadow-xl">
                  <div className="flex h-[58%] w-[58%] items-center justify-center rounded-2xl bg-green-500">
                    <span className="flex h-[54%] w-[54%] items-center justify-center rounded-full bg-white text-3xl leading-none text-green-500">
                      ✓
                    </span>
                  </div>
                </div>
              </div>

              <h2 className="mt-5 text-center text-[clamp(20px,6vw,26px)] font-black leading-tight text-slate-950">
                {t("user.feedback.successTitle")}
              </h2>
              <p className="mt-5 text-lg font-bold text-blue-500">
                {t("user.feedback.successThanks")}
              </p>
              <p className="mt-2 text-center text-[15px] leading-7 text-slate-500">
                {t("user.feedback.successDetail1")}
                <br />
                {t("user.feedback.successDetail2")}
              </p>

              <button
                onClick={closeFeedback}
                className="absolute bottom-[calc(env(safe-area-inset-bottom)+18px)] left-4 right-4 flex h-16 items-center justify-center gap-3 rounded-xl bg-blue-600 font-black text-white"
              >
                <MapIcon className="w-5 h-5 shrink-0" />
                {t("user.feedback.backToMap")}
              </button>
            </div>
          ) : (
            <div className="mx-auto flex h-[100dvh] w-full max-w-[430px] flex-col overflow-hidden bg-slate-50 md:h-[min(860px,calc(100vh-32px))] md:rounded-2xl md:shadow-2xl">
              <div className="relative flex h-[calc(env(safe-area-inset-top)+72px)] shrink-0 items-end justify-center bg-white px-5 pb-5">
                <button
                  onClick={closeFeedback}
                  className="absolute bottom-3 left-5 grid h-10 w-10 place-items-center rounded-full text-3xl leading-none text-slate-500 hover:bg-slate-100"
                  aria-label="ปิด"
                >
                  ×
                </button>
                <h2 className="text-lg font-bold text-slate-900">
                  {t("user.feedback.title")}
                </h2>
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-[calc(env(safe-area-inset-bottom)+24px)]">
                <div className="mt-4 text-base font-bold text-slate-900">
                  {t("user.feedback.selectProblem")}
                </div>

                <div className="mt-3 grid grid-cols-2 gap-3">
                  {feedbackOptions.map((item) => {
                    const selected = feedbackType === item.id;
                    const Icon = item.Icon;
                    return (
                      <button
                        key={item.id}
                        onClick={() => setFeedbackType(item.id)}
                        className={`flex aspect-[1.35] min-h-[112px] items-center justify-center rounded-2xl border-2 bg-white p-3 shadow-sm ${selected ? "border-blue-600" : "border-transparent"
                          }`}
                      >
                        <span className="flex flex-col items-center justify-center">
                          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-blue-50 text-2xl font-bold text-blue-600">
                            <Icon className="h-7 w-7 text-blue-600" />
                          </span>
                          <span className="mt-3 text-center text-sm font-bold leading-tight text-slate-600 sm:text-base">
                            {item.label}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>

                {/* การ์ดเลือกตำแหน่งทางปิด (โผล่เฉพาะตอนเลือก "ทางปิด") */}
                {isClosedRoute && (
                  <button
                    type="button"
                    onClick={() => setClosurePickerOpen(true)}
                    className={`mt-4 w-full rounded-2xl border-2 ${closureSelection
                      ? "border-red-300 bg-red-50"
                      : "border-dashed border-red-300 bg-white"
                      } p-3 text-left flex items-center gap-3`}
                  >
                    <BarrierIcon className="h-7 w-7 text-red-700 ml-1 mr-2" />
                    <div className="flex-1 min-w-0">
                      {closureSelection ? (
                        <>
                          <div className="font-bold text-red-700 text-sm">
                            เลือกตำแหน่งแล้ว
                          </div>
                          <div className="text-xs text-slate-600 truncate">
                            {translateText(closureSelection.buildingName)} •{" "}
                            {translateText(closureSelection.floorName)} (
                            {Math.round(closureSelection.x)},{" "}
                            {Math.round(closureSelection.y)})
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="font-bold text-red-700 text-sm">
                            เลือกตำแหน่งทางปิดบนแผนที่
                          </div>
                          <div className="text-xs font-semibold text-slate-600">
                            แตะตรงนี้แล้วจิ้มจุดที่ทางปิด
                          </div>
                        </>
                      )}
                    </div>
                    <span className="text-red-700 text-sm font-bold">
                      {closureSelection ? "แก้ไข" : "เลือก ›"}
                    </span>
                  </button>
                )}

                <div className="mt-6 text-base font-bold text-slate-900">
                  {t("user.feedback.attachImageOptional")}
                </div>
                <label className="relative mt-3 block aspect-[1.95] max-h-52 min-h-[160px] cursor-pointer overflow-hidden rounded-xl bg-slate-200">
                  {feedbackImage ? (
                    <img
                      src={URL.createObjectURL(feedbackImage)}
                      alt=""
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="flex h-full w-full flex-col items-center justify-center bg-slate-100 text-slate-400">
                      <CameraIcon className="h-10 w-10 text-slate-400" />
                    </div>
                  )}
                  <div className="absolute bottom-4 left-1/2 flex h-10 w-[min(78%,310px)] -translate-x-1/2 items-center justify-center gap-3 rounded-xl bg-white/85 px-4 font-bold text-blue-600">
                    <CameraIcon className="h-4 w-4 text-blue-600" />
                    <span className="truncate">{t("user.feedback.attachImage")}</span>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => setFeedbackImage(e.target.files?.[0] || null)}
                    />
                  </div>
                </label>

                <div className="mt-6 text-base font-bold text-slate-900">
                  {t("user.feedback.additionalDetail")}
                </div>
                <textarea
                  value={feedbackMessage}
                  onChange={(e) => setFeedbackMessage(e.target.value)}
                  placeholder="ตัวอย่าง: รพ.มหาราชนครเชียงใหม่ ปรับปรุงระบบลิฟต์ใหม่"
                  className="mt-3 min-h-[110px] w-full rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-700 outline-none resize-none placeholder:text-slate-400"
                />

                {feedbackError && (
                  <div className="mt-2 text-sm font-bold text-red-600">
                    {feedbackError}
                  </div>
                )}

                <button
                  onClick={submitFeedback}
                  disabled={feedbackSubmitting}
                  className="mt-5 flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-blue-600 text-base font-semibold text-white disabled:opacity-60 sm:text-lg"
                >
                  {feedbackSubmitting ? t("user.feedback.submitting") : `${t("user.feedback.submitToStaff")}`}
                  <SendIcon className="h-3 w-3 text-white -mt-0.5" />
                </button>
              </div>
            </div>
          )}
        </div>,
        document.body
      )}

      <RouteChoiceModal
        isOpen={routeModalOpen}
        onClose={() => {
          setRouteModalOpen(false);
          setPendingDestination(null);
        }}
        onSelectMode={handleSelectRouteMode}
        selectedMode={routePreference}
        destinationName={
          (pendingDestination || activeMarker?.data)
            ? parseNames((pendingDestination || activeMarker.data).name).primary
            : ""
        }
      />

    </div>
  );
}

function getSessionId() {
  const KEY = "indoor_nav_session_id";
  let sid = localStorage.getItem(KEY);
  if (!sid) {
    sid =
      (crypto?.randomUUID && crypto.randomUUID()) ||
      `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(KEY, sid);
  }
  return sid;
}
